import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import mapStyles from "~/features/salesPartner/components/MapSection/Map/mapStyles";
import GmapsTypeButtons from "~/features/salesPartner/components/MapSection/Map/MapButtons";
import { type CenterValue, type GoogleMarker } from "~/domains/salesPartner/types/SalesPartnerTypes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import classNames from "classnames";
import { IMG_MARKER, IMG_MARKER_ACTIVE } from "~/utils/url/staticResources";

interface Props {
  className?: string;
  markers: GoogleMarker[];
  selectedMarker: GoogleMarker | undefined;
  center: CenterValue;
  zoomValue: number;
  setZoomValue: (newVal: number) => void;
  selectSalesPartner: (marker: GoogleMarker | undefined, canOpenPopup?: boolean) => void;
  children?: ReactNode;
}

export enum DisplayType {
  Schaeffler,
  Map,
  Satellite,
}

const MAX_POSSIBLE_ZOOM = 22;
const MAX_AUTOMATIC_ZOOM = MAX_POSSIBLE_ZOOM - 7;

export const SPMap: FC<Props> = (props) => {
  const { markers, selectedMarker, center, zoomValue, setZoomValue, selectSalesPartner, children, className } = props;

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [displayType, setDisplayType] = useState<DisplayType>(DisplayType.Schaeffler);
  const isDesktop = useIsMinDesktopSize();

  const options: google.maps.MapOptions = window.google
    ? {
        styles: displayType === DisplayType.Schaeffler ? mapStyles : undefined,
        mapTypeId:
          displayType === DisplayType.Satellite
            ? window.google.maps.MapTypeId.SATELLITE
            : window.google.maps.MapTypeId.ROADMAP,

        disableDefaultUI: true,
        streetViewControl: isDesktop,
        streetViewControlOptions: {
          position: window.google.maps.ControlPosition.LEFT_BOTTOM,
        },
        fullscreenControl: isDesktop,
        fullscreenControlOptions: {
          position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        mapTypeControl: false,
        mapTypeControlOptions: {
          position: window.google.maps.ControlPosition.BOTTOM_CENTER,
        },
        zoomControl: isDesktop,
        gestureHandling: isDesktop ? undefined : "greedy",
      }
    : {};

  const onLoad = setMap;

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  useEffect(() => {
    if (!!window.google?.maps?.LatLngBounds && markers.length > 0 && map) {
      const bounds = new window.google.maps.LatLngBounds();
      for (let i = 0; i < markers.length; i++) {
        bounds.extend(markers[i]);
      }
      map.fitBounds(bounds, { left: 10, right: 10, bottom: 20, top: 20 });
      google.maps.event.addListenerOnce(map, "idle", function () {
        if ((map.getZoom() ?? 1) > MAX_AUTOMATIC_ZOOM) {
          map.setZoom(MAX_AUTOMATIC_ZOOM);
        }
      });
    }
  }, [markers]);

  return (
    <div className={classNames("scha__sp__map", className)}>
      <GoogleMap
        mapContainerStyle={CONTAINER_STYLE}
        options={options}
        center={center}
        zoom={zoomValue}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={() => {
          if (map !== null) {
            setZoomValue(map.getZoom() ?? 1);
          }
        }}
      >
        {markers.map((marker) => (
          <Marker
            key={marker.unicId}
            position={{ lat: marker.lat, lng: marker.lng }}
            onClick={() => selectSalesPartner(marker, false)}
            icon={selectedMarker && marker.unicId === selectedMarker.unicId ? ACTIVE_MARKER_ICON : MARKER_ICON}
          />
        ))}
        {isDesktop && <GmapsTypeButtons onButtonClick={setDisplayType} activeDisplayType={displayType} />}

        {children}
      </GoogleMap>
    </div>
  );
};

const CONTAINER_STYLE = {
  width: "100%",
  height: "100%",
};

const MARKER_ICON = {
  url: IMG_MARKER,
};

const ACTIVE_MARKER_ICON = {
  url: IMG_MARKER_ACTIVE,
};
